import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useChannelsDropdown } from "@/modules/channels/hooks/useChannelsDropdown";
import {
  MetricCard,
  PerformanceChart,
  UpcomingAppointmentsWidget,
} from "../components";
import {
  usePerformanceData,
  useUpcomingAppointments,
  useDashboardMetrics,
} from "../hooks";
import {
  SkeletonCard,
  PageToolbar,
  Breadcrumb,
  PeriodPicker,
  Select,
} from "@/shared/components/ui";
import type { PeriodPickerValue } from "@/shared/components/ui";
import { Skeleton } from "@/shared/components/ui/Skeleton";
import { Icon } from "@/shared/icons";
import { formatTimeAgo } from "@/lib/utils";

/**
 * Página principal do dashboard (TIPO: Analytics)
 *
 * PageToolbar (contrato 3.3): breadcrumb à esquerda · Canal + Período
 * + atualizar à direita — canal e período são controles diretos. O canal
 * filtra todos os cards e o gráfico ("Todos os canais" = consolidado); o
 * período é o shared PeriodPicker (F81 — inclui Personalizado; default = mês,
 * igual às dashs comercial e de marketing). Corpo: grid de KPIs (hero), gráfico + agenda,
 * grid de KPIs secundários, e nota de histórico fechado (rodapé). Tokenizado
 * (var(--theme-*)), empilha no mobile.
 */
export const DashboardPage: React.FC = () => {
  const [period, setPeriod] = useState<PeriodPickerValue>({ mode: 'month' });
  // '' = todos os canais (consolidado)
  const [channelId, setChannelId] = useState<string>('');
  const selectedChannelId = channelId || undefined;

  const { data: channels } = useChannelsDropdown();
  const channelOptions = useMemo(
    () => [
      { value: '', label: 'Todos os canais' },
      ...(channels ?? []).map((channel) => ({ value: channel.id, label: channel.name })),
    ],
    [channels]
  );

  const {
    data: performance,
    isLoading: performanceLoading,
    isFetching: performanceFetching,
    refetch: refetchPerformance,
  } = usePerformanceData(period, selectedChannelId);
  const {
    data: appointments,
    isLoading: appointmentsLoading,
    isFetching: appointmentsFetching,
    refetch: refetchAppointments,
  } = useUpcomingAppointments(4);
  const {
    data: currentMetrics,
    isLoading: metricsLoading,
    isFetching: metricsFetching,
    refetch: refetchMetrics,
    dataUpdatedAt: metricsUpdatedAt,
  } = useDashboardMetrics(period, selectedChannelId);

  const handleMetricClick = (_metricId: string) => {
    // TODO: Implementar navegação ou modal com detalhes da métrica
  };

  const navigate = useNavigate();

  const handleViewAllAgenda = () => {
    navigate('/calendar');
  };

  const handleRefresh = () => {
    void refetchMetrics();
    void refetchPerformance();
    void refetchAppointments();
  };

  const isRefreshing = metricsFetching || performanceFetching || appointmentsFetching;

  // Separar primeiras 4 métricas como hero e restante como secondary
  const heroMetrics = currentMetrics?.slice(0, 4) || [];
  const secondaryMetrics = currentMetrics?.slice(4) || [];

  return (
    <div
      className="h-full flex flex-col"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
    >
      {/* Toolbar única do DS (F23 — PageToolbar 56px, token header) */}
      <PageToolbar
        data-testid="dashboard-toolbar"
        left={
          <Breadcrumb
            items={[
              { label: 'Dashboards' },
              { label: 'Atendimento', testid: 'dashboard-breadcrumb-current' },
            ]}
            mobileLegend="Dashboards"
            data-testid="dashboard-breadcrumb"
          />
        }
        right={
          <div className="flex items-center gap-2 flex-nowrap overflow-x-auto sm:flex-wrap sm:overflow-visible">
            {/* Canal — filtra todos os cards e o gráfico */}
            <div className="flex-shrink-0" style={{ width: 200 }}>
              <Select
                value={channelId}
                onChange={setChannelId}
                options={channelOptions}
                aria-label="Canal de atendimento"
                title="Canal de atendimento"
                style={{ height: 36 }}
                data-testid="dashboard-channel"
              />
            </div>
            {/* Período — controle direto, shared PeriodPicker (F81, com Personalizado) */}
            <PeriodPicker
              value={period}
              onChange={setPeriod}
              data-testid="dashboard-period"
            />
            {/* Atualizar — 36px bordado, ícone 16, com hover (F07) */}
            <button
              type="button"
              data-testid="dashboard-refresh"
              onClick={handleRefresh}
              aria-label="Atualizar"
              title="Atualizar"
              className="w-9 h-9 rounded-lg border flex items-center justify-center flex-shrink-0 transition-colors"
              style={{
                borderColor: 'var(--theme-input-border)',
                backgroundColor: 'var(--theme-card-background)',
                color: 'var(--theme-muted-text)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-card-background)';
              }}
            >
              <Icon
                name="refresh"
                size={16}
                className={isRefreshing ? 'animate-spin' : ''}
              />
            </button>
          </div>
        }
      />

      {/* Scroll */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6 space-y-4">
          {/* Hero KPI Cards - 4 cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {metricsLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))
            ) : heroMetrics.length > 0 ? (
              heroMetrics.map((metric) => (
                <MetricCard
                  key={metric.id}
                  metric={metric}
                  onClick={handleMetricClick}
                />
              ))
            ) : (
              <div
                className="sm:col-span-2 lg:col-span-4 h-24 rounded-lg flex items-center justify-center border"
                style={{
                  backgroundColor: 'var(--theme-card-background)',
                  borderColor: 'var(--theme-card-border)',
                }}
                data-testid="dashboard-metrics-empty"
              >
                <span style={{ color: 'var(--theme-muted-text)' }}>
                  Nenhuma métrica disponível
                </span>
              </div>
            )}
          </div>

          {/* Middle: gráfico (3/5) + agenda (2/5) */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Performance Chart */}
            <div className="lg:col-span-3">
              {performanceLoading ? (
                <div
                  className="rounded-lg border p-4"
                  style={{
                    backgroundColor: 'var(--theme-card-background)',
                    borderColor: 'var(--theme-card-border)',
                    minHeight: '380px',
                  }}
                >
                  <Skeleton variant="text" width="40%" className="mb-2" />
                  <Skeleton variant="text" width="60%" className="mb-6" />
                  <Skeleton variant="rectangular" height={260} />
                </div>
              ) : performance ? (
                <PerformanceChart data={performance.items} period={performance.period} height={280} />
              ) : (
                <div
                  className="rounded-lg flex items-center justify-center border"
                  style={{
                    backgroundColor: 'var(--theme-card-background)',
                    borderColor: 'var(--theme-card-border)',
                    minHeight: '380px',
                  }}
                  data-testid="dashboard-chart-empty"
                >
                  <span style={{ color: 'var(--theme-muted-text)' }}>
                    Dados não disponíveis
                  </span>
                </div>
              )}
            </div>

            {/* Upcoming Appointments */}
            <div className="lg:col-span-2">
              {appointmentsLoading ? (
                <div
                  className="rounded-lg border p-4 space-y-4"
                  style={{
                    backgroundColor: 'var(--theme-card-background)',
                    borderColor: 'var(--theme-card-border)',
                    minHeight: '380px',
                  }}
                >
                  <Skeleton variant="text" width="50%" />
                  <Skeleton variant="text" width="65%" />
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Skeleton variant="circular" width={32} height={32} />
                      <div className="flex-1">
                        <Skeleton variant="text" width="75%" />
                        <Skeleton variant="text" width="40%" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : appointments ? (
                <UpcomingAppointmentsWidget
                  appointments={appointments}
                  maxItems={4}
                  onViewAll={handleViewAllAgenda}
                />
              ) : (
                <div
                  className="rounded-lg flex items-center justify-center border"
                  style={{
                    backgroundColor: 'var(--theme-card-background)',
                    borderColor: 'var(--theme-card-border)',
                    minHeight: '380px',
                  }}
                  data-testid="dashboard-appointments-empty"
                >
                  <span style={{ color: 'var(--theme-muted-text)' }}>
                    Nenhum compromisso
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Secondary Metrics Row */}
          {!metricsLoading && secondaryMetrics.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
              {secondaryMetrics.map((metric) => (
                <MetricCard
                  key={metric.id}
                  metric={metric}
                  onClick={handleMetricClick}
                />
              ))}
            </div>
          )}

          {/* Nota — histórico fechado */}
          <div
            data-testid="dashboard-closed-history-note"
            className="flex items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-xs"
            style={{
              backgroundColor: 'var(--theme-surface-sunken)',
              borderColor: 'var(--theme-card-border)',
              color: 'var(--theme-muted-text)',
            }}
          >
            <Icon
              name="info"
              className="w-4 h-4 flex-shrink-0"
              style={{ color: 'var(--theme-muted-text)', opacity: 0.7 }}
            />
            <span>
              <b style={{ color: 'var(--theme-card-text)', fontWeight: 600 }}>
                Tempos, SLA e abandono
              </b>{' '}
              consideram só conversas encerradas
              {metricsUpdatedAt
                ? ` · atualizado ${formatTimeAgo(new Date(metricsUpdatedAt))}`
                : ''}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

DashboardPage.displayName = 'DashboardPage';
